import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { 
  DollarSign, 
  ShoppingCart, 
  Package, 
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { getAuthHeaders } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [statsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/admin/orders/stats`, { headers }),
          axios.get(`${API}/admin/orders?limit=5`, { headers })
        ]);
        setStats(statsRes.data);
        setRecentOrders(ordersRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      label: "Today's Orders",
      value: stats?.today_orders || 0,
      icon: ShoppingCart,
      color: '#D4540A'
    },
    {
      label: "Today's Revenue",
      value: `$${(stats?.today_revenue || 0).toFixed(2)}`,
      icon: DollarSign,
      color: '#2D5016'
    },
    {
      label: 'Pending Fulfillment',
      value: stats?.pending_fulfillment || 0,
      icon: Package,
      color: '#1E4D6B',
      highlight: (stats?.pending_fulfillment || 0) > 0
    },
    {
      label: 'Top SKU',
      value: stats?.top_sku || 'N/A',
      subvalue: stats?.top_sku_count ? `${stats.top_sku_count} sold` : '',
      icon: TrendingUp,
      color: '#5C3D2E'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      payment_pending: 'bg-yellow-500/20 text-yellow-400',
      paid: 'bg-green-500/20 text-green-400',
      fulfilled: 'bg-blue-500/20 text-blue-400',
      cancelled: 'bg-red-500/20 text-red-400',
      refunded: 'bg-gray-500/20 text-gray-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-[#888]">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, idx) => (
          <div
            key={idx}
            className={`bg-[#1a1a1a] rounded-xl p-5 border ${
              stat.highlight ? 'border-[#D4540A]' : 'border-[#2a2a2a]'
            }`}
            data-testid={`stat-card-${idx}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}20` }}
              >
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
              </div>
              {stat.highlight && (
                <span className="text-xs font-medium text-[#D4540A] bg-[#D4540A]/10 px-2 py-1 rounded">
                  Action needed
                </span>
              )}
            </div>
            <p className="text-[#888] text-sm mb-1">{stat.label}</p>
            <p className="text-white text-2xl font-bold">{stat.value}</p>
            {stat.subvalue && (
              <p className="text-[#666] text-xs mt-1">{stat.subvalue}</p>
            )}
          </div>
        ))}
      </div>

      {/* Low Stock Alert */}
      {stats?.low_stock_items?.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <h3 className="font-semibold text-yellow-500">Low Stock Alert</h3>
          </div>
          <div className="space-y-2">
            {stats.low_stock_items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-white">{item.name}</span>
                <span className="text-yellow-400">{item.qty} remaining</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a]">
          <div className="flex items-center justify-between p-4 border-b border-[#2a2a2a]">
            <h2 className="font-semibold text-white">Recent Orders</h2>
            <Link
              to="/admin/orders"
              className="text-sm text-[#D4540A] hover:text-[#B84709] flex items-center gap-1"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-[#2a2a2a]">
            {recentOrders.length === 0 ? (
              <div className="p-8 text-center text-[#666]">
                No orders yet
              </div>
            ) : (
              recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/admin/orders/${order.order_id || order.id}`}
                  className="flex items-center justify-between p-4 hover:bg-[#222] transition-colors"
                >
                  <div>
                    <p className="font-medium text-white">
                      {order.order_id || order.id.slice(0, 8)}
                    </p>
                    <p className="text-sm text-[#666]">
                      {order.customer_name || `${order.shipping?.first_name} ${order.shipping?.last_name}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-white">
                      ${(order.totals?.total || order.total || 0).toFixed(2)}
                    </p>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(order.status)}`}>
                      {order.status?.replace('_', ' ')}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a]">
          <div className="p-4 border-b border-[#2a2a2a]">
            <h2 className="font-semibold text-white">Quick Overview</h2>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-[#2a2a2a]">
              <span className="text-[#888]">Total Orders</span>
              <span className="text-white font-semibold">{stats?.total_orders || 0}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-[#2a2a2a]">
              <span className="text-[#888]">Total Revenue</span>
              <span className="text-white font-semibold">${(stats?.total_revenue || 0).toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-[#888]">Avg Order Value</span>
              <span className="text-white font-semibold">
                ${stats?.total_orders > 0 ? ((stats?.total_revenue || 0) / stats.total_orders).toFixed(2) : '0.00'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
